import { ConsumptionSiteDisplayMode, FulfillmentHandling, FulfillmentTimeMode } from "@/data/model/Enums";
import type { IOrderUseCase } from "@/data/model/OrderUseCase";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";

import { TastierClient } from "@/app/TastierClient";
import { RealtimeFulfillmentList } from "@/app/elements/RealtimeFulfillmentList";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { Helper } from "@/app/ui/Helper";
import { LocationAddressPopup } from "@/app/ui/LocationAddressPopup";
import { DateTimeUtils } from "@/app/util/DateTimeUtils";

export interface IFulfillmentPopoverData {
    orderNumber: string;
    orderId: string;
    orderTime: Date;
    locationName: string;
    tableName: string;
    orderServiceId?: string;
    preorder: boolean;
    orderScheduledFulfillmentDateTime?: Date;
}

export class FulfillmentPopover extends BasePopover {
    private readonly fulfillments: RealtimeFulfillmentList;
    private readonly data: IFulfillmentPopoverData;
    private readonly useCase?: IOrderUseCase;

    private readonly $useCase: JQuery;
    private readonly $preorderInfo: JQuery;

    constructor(data: IFulfillmentPopoverData, uniqueId?: string) {
        super(trans.fulfillmentPopover.title, false, uniqueId);
        this.$element.addClass("fulfillment");

        this.data = data;
        this.useCase = Helper.getUseCaseByOrderOrOfferServiceId(data.orderServiceId);

        const head = $('<div class="orderinfo" />').appendTo(this.$body);
        head.append('<div class="location">' + Format.htmlEscape(data.locationName) + "</div>");
        head.append('<div class="ordernumber">' + data.orderNumber + "</div>");

        const useCase = Helper.getUseCaseByOrderOrOfferServiceId(data.orderServiceId);

        this.$preorderInfo = $('<div class="pre-order"></div>').appendTo(this.$body);

        this.setPickUpDateTimeSection(data.orderScheduledFulfillmentDateTime);

        if (useCase?.consumptionSiteDisplayMode === ConsumptionSiteDisplayMode.LocationAddress) {
            const locationName = $(
                '<div class="location-name"><b><u>' +
                    TastierClient.context.currentAllocation!.locationName +
                    "</u></b></div>"
            ).appendTo(this.$body);
            locationName.click((e) => {
                e.preventDefault();

                const locationAddress = TastierClient.context.currentAllocation?.locationAddress;
                const googleMapsLocationLink = TastierClient.context.currentAllocation?.googleMapsLocationLink;
                if (locationAddress != null) {
                    LocationAddressPopup.show(locationAddress, googleMapsLocationLink, undefined);
                }
            });
        }

        this.$useCase = $('<div class="usecase"></div>').appendTo(this.$body);

        this.setUseCaseSection(data.tableName, this.useCase, false, false);

        this.fulfillments = new RealtimeFulfillmentList(data.orderId).renderTo(this.$body);
        this.fulfillments.onListChanged = () => this.onFulfillmentListChanged();

        this.updateBodyHeight();
    }

    protected onShow() {
        this.fulfillments.startListening();
        TastierClient.context.onFulfillmentsChanged.add(this.onFulfillmentsChanged, this);
    }

    protected onHide() {
        this.destroy();
    }

    protected onDestroy() {
        TastierClient.context.onFulfillmentsChanged.remove(this.onFulfillmentsChanged, this);
        this.fulfillments.stopListening();
    }

    private onFulfillmentsChanged() {
        const orderPickUpDateTime = TastierClient.context.getActiveOrderPickUpDateTime(this.data.orderId);
        this.setPickUpDateTimeSection(orderPickUpDateTime);
    }

    private setPickUpDateTimeSection(orderScheduledFulfillmentDateTime?: Date) {
        // Clear the section
        this.$preorderInfo.empty();

        const useCase = Helper.getUseCaseByOrderOrOfferServiceId(this.data.orderServiceId);
        if (
            useCase?.fulfillmentTimeMode != null &&
            useCase?.fulfillmentTimeMode !== FulfillmentTimeMode.None &&
            orderScheduledFulfillmentDateTime != null
        ) {
            this.$preorderInfo.show();
            if (useCase.fulfillmentTimeMode === FulfillmentTimeMode.Time) {
                this.$preorderInfo.append(
                    `<div class="pick-up-time"><b>${DateTimeUtils.toDateTimeWithDayOfWeekAndCirca(orderScheduledFulfillmentDateTime, false)}</b></div>`
                );
            } else {
                this.$preorderInfo.append(
                    `<div class="pick-up-time">${trans.orderReceivedPopover.plannedPickUpTime}: <b>${DateTimeUtils.toDateTimeWithDayOfWeekAndCirca(orderScheduledFulfillmentDateTime)}</b></div>`
                );
            }
        } else {
            this.$preorderInfo.hide();
        }
    }

    private setUseCaseSection(
        tableName: string,
        useCase: IOrderUseCase | undefined,
        isAnyFulfillment: boolean,
        isAnyFulfillmentReady: boolean
    ) {
        if (useCase !== undefined && useCase.orderPlacedText !== null && useCase.orderPlacedText !== undefined) {
            this.$useCase.show();
            if (isAnyFulfillment) {
                if (isAnyFulfillmentReady) {
                    this.$useCase.text(trans.fulfillmentPopover.showWhenPickingUpOrder);
                } else {
                    if (TastierClient.context.isPushNotificationsReady(useCase) === true) {
                        const text = TastierClient.context.getOrderPlacedMessageIfNotificationEnabled(useCase);
                        this.$useCase.text(text);
                    } else {
                        const t = Format.translate(useCase.orderPlacedText, clientLanguage()).replace(
                            "[tablename]",
                            tableName
                        );
                        this.$useCase.text(t);
                    }
                }
            } else {
                if (useCase?.fulfillmentHandling === FulfillmentHandling.OperatorAndApp) {
                    this.$useCase.hide();
                    this.$useCase.text("");
                } else {
                    const t = Format.translate(useCase.orderPlacedText, clientLanguage()).replace(
                        "[tablename]",
                        tableName
                    );
                    this.$useCase.text(t);
                }
            }
        } else {
            this.$useCase.hide();
            this.$useCase.text("");
        }
    }

    private updateTitle(isAnyFulfillment: boolean, isAnyFulfillmentReady: boolean) {
        if (isAnyFulfillment && isAnyFulfillmentReady) {
            this.setTitle(trans.fulfillmentPopover.readyForPickUp);
        } else {
            this.setTitle(trans.fulfillmentPopover.title);
        }
    }

    private onFulfillmentListChanged() {
        const isAnyFulfillmentReady = this.fulfillments != null && this.fulfillments.hasAnyFulfillmentReady();
        const isAnyFulfillment = this.fulfillments.hasAnyFulfillment();

        this.updateTitle(isAnyFulfillment, isAnyFulfillmentReady);
        this.setUseCaseSection(this.data.tableName, this.useCase, isAnyFulfillment, isAnyFulfillmentReady);
        this.updateBodyHeight();

        if (!this.fulfillments.hasAnyFulfillment()) {
            setTimeout(() => {
                this.hide();
            }, 800);
        }
    }

    // private showLocationAddressPopup(locationAddress: IAddress, googleMapsLocationLink: string | undefined) {
    //     const locationAddressStr = (locationAddress.street ?? "") + ", " + (locationAddress.zip ?? "") + " " + (locationAddress.city ?? "");
    //     const isGoogleMapsLocationLinkAvailable = googleMapsLocationLink != null && googleMapsLocationLink.length > 0;
    //
    //     Popup.show(locationAddressStr,
    //         () => {
    //             if (isGoogleMapsLocationLinkAvailable) {
    //                 window.open(googleMapsLocationLink);
    //             } else {
    //                 try {
    //                     await navigator.clipboard.writeText(locationAddressStr);
    //                 } catch (ex) {
    //                     logError(ex);
    //                     Popup.show(trans.addToHomeScreen.copyCodeFailed);
    //                 }
    //             }
    //         }, isGoogleMapsLocationLinkAvailable ? trans.orderReceivedPopover.googleMapsLocation : trans.orderReceivedPopover.copyAddress, undefined, undefined, undefined, undefined, undefined, undefined, true);
    // }
}
